import Vue from 'vue'
import VueRouter from 'vue-router'
import request from '@/utils/request'   // 任务C：403/404 区分（全量菜单路径）
import Login from '@/views/login/Login.vue'
import Home from '@/views/Home.vue'
import Workbench from '@/views/workbench/index.vue'


Vue.use(VueRouter)

// ===== 静态路由（不依赖权限，始终存在） =====
const constantRoutes = [
  { path: '/', component: Home },
  { path: '/login', component: Login },
  { path: '/workbench', component: Workbench, meta: { requiresAuth: true, title: '工作台' } },
  { path: '/system/api-logs', component: () => import('@/views/api-logs/index'), meta: { requiresAuth: true, title: '接口日志' } },
  { path: '/system/mes-sync-monitor', component: () => import('@/views/mesSyncMonitor/index'), meta: { requiresAuth: true, title: 'MES 同步监控' } },
  { path: '/purchaseDeliveryNote', component: () => import('@/views/purchaseDeliveryNote/index.vue'), meta: { requiresAuth: true, title: '采购送货单' } },
  { path: '/salesDeliveryNote', component: () => import('@/views/salesDeliveryNote/index.vue'), meta: { requiresAuth: true, title: '销售送货单' } },
  { path: '/materialConfig', component: () => import('@/views/materialConfig/index.vue'), meta: { requiresAuth: true, title: '物料仓储属性配置' } },
  { path: '/venusInstance', component: () => import('@/views/venusInstance/index.vue'), meta: { requiresAuth: true, title: '维纳斯件管理' } },
  { path: '/venusInstance/detail', component: () => import('@/views/venusInstance/detail.vue'), meta: { requiresAuth: true, title: '维纳斯件详情' } },
  { path: '/bom', component: () => import('@/views/bom/index.vue'), meta: { requiresAuth: true, title: 'BOM管理' } },
  { path: '/bom/detail', component: () => import('@/views/bom/detail.vue'), meta: { requiresAuth: true, title: 'BOM详情' } },
  { path: '/productionOrder', component: () => import('@/views/productionOrder/index.vue'), meta: { requiresAuth: true, title: '生产订单' } },
  { path: '/productionOrder/detail', component: () => import('@/views/productionOrder/detail.vue'), meta: { requiresAuth: true, title: '生产订单详情' } },
  // A-2 阶段1：工艺路线（静态注册；菜单表 permission 183/184/185 同步配置）
  { path: '/production/routing', component: () => import('@/views/production/routing/index.vue'), meta: { requiresAuth: true, title: '工艺路线' } },
  { path: '/production/routing-material-assign/:routingId', component: () => import('@/views/production/routing-material-assign/index.vue'), meta: { requiresAuth: true, title: '工序物料分配' } },
  // A-5 段 2：生产订单（静态注册；菜单表 permission workOrder:view 同步配置）
  { path: '/production/work-order', component: () => import('@/views/production/workOrder/index.vue'), meta: { requiresAuth: true, title: '生产订单' } },
  { path: '/production/work-order/detail', component: () => import('@/views/production/workOrder/detail.vue'), meta: { requiresAuth: true, title: '生产订单详情' } },
  { path: '/rejectedApproval', component: () => import('@/views/rejectedModify/index.vue'), meta: { requiresAuth: true, title: '待修改单据' } },
  { path: '/approve', component: () => import('@/views/approve/index.vue'), meta: { requiresAuth: true, title: '审批管理' } },
  { path: '/pendingTasks', component: () => import('@/views/pendingTasks/index.vue'), meta: { requiresAuth: true, title: '待处理' } },
  { path: '/rejectedModify', component: () => import('@/views/rejectedModify/index.vue'), meta: { requiresAuth: true, title: '待修改' } },
  { path: '/freeOutbound', component: () => import('@/views/freeOutbound/index.vue'), meta: { requiresAuth: true, title: '库存调整出库' } },
  // 任务C：无权限/不存在路由兜底页
  { path: '/403', component: () => import('@/views/error/403.vue'), meta: { title: '无权限' } },
  { path: '/404', component: () => import('@/views/error/404.vue'), meta: { title: '页面不存在' } }
]

// ===== 创建路由实例 =====
const createRouter = () => new VueRouter({
  mode: 'history',
  base: process.env.BASE_URL,
  routes: constantRoutes
})

let router = createRouter()
let routesLoaded = false

// ===== 重置路由（只替换 matcher，保留实例和守卫） =====
export function resetRouter() {
  const newRouter = createRouter()
  router.matcher = newRouter.matcher  // ✅ 关键修复：只替换匹配器，不重新赋值整个 router
}

// ===== 递归生成路由 =====
function generateRoutes(menuTree) {
  const routes = []
  menuTree.forEach(menu => {
    // 只有 path 和 component 都存在且不为空时才生成路由
    if (menu.path && menu.component) {
      routes.push({
        path: menu.path,
        name: menu.name || menu.code,
        component: () => import(`@/${menu.component}`),
        meta: { requiresAuth: true, title: menu.name }
      })
    }
    if (menu.children && menu.children.length) {
      routes.push(...generateRoutes(menu.children))
    }
  })
  return routes
}

// ===== 添加动态路由 =====
export function addDynamicRoutes(menuTree) {
  resetRouter()
  const newRoutes = generateRoutes(menuTree)
  router.addRoutes(newRoutes)
  routesLoaded = true
}

// ===== 重置路由加载状态（退出登录时调用） =====
export function resetRouteState() {
  routesLoaded = false
  allMenuPathsCache = null   // 任务C：清 403/404 区分缓存
  try { sessionStorage.removeItem('allMenuPaths') } catch (e) { /* ignore */ }
}

// ===== 任务C：全量 PC 菜单路径（403/404 区分——仅路由未匹配时拉取并缓存） =====
let allMenuPathsCache = null
async function loadAllMenuPaths() {
  if (allMenuPathsCache) return allMenuPathsCache
  try {
    const cached = sessionStorage.getItem('allMenuPaths')
    if (cached) { allMenuPathsCache = JSON.parse(cached); return allMenuPathsCache }
  } catch (e) { /* ignore */ }
  try {
    const tree = await request.get('/api/wms/permission/tree')
    const paths = []
    const walk = ns => (ns || []).forEach(n => {
      if (n.path) paths.push(n.path)
      if (n.children && n.children.length) walk(n.children)
    })
    walk(tree || [])
    allMenuPathsCache = paths
    sessionStorage.setItem('allMenuPaths', JSON.stringify(paths))
  } catch (e) { allMenuPathsCache = [] }
  return allMenuPathsCache
}

// ===== 全局前置守卫 =====
router.beforeEach(async (to, from, next) => {
  const token = localStorage.getItem('token')
  const publicPages = ['/', '/login']

  // 1. 未登录 → 跳转登录
  if (!publicPages.includes(to.path) && !token) {
    return next('/login')
  }

  // 2. 已登录访问登录页或首页 → 跳转工作台
  if (token && (to.path === '/login' || to.path === '/')) {
    return next('/workbench')
  }

  // 3. F5刷新时，如果动态路由还没加载，从 localStorage 读取菜单树
  if (token && !routesLoaded) {
    const menuTreeStr = localStorage.getItem('menuTree')
    if (menuTreeStr) {
      try {
        const menuTree = JSON.parse(menuTreeStr)
        if (Array.isArray(menuTree) && menuTree.length) {
          addDynamicRoutes(menuTree)
          // 重新导航到目标路由，确保动态路由生效
          return next({ ...to, replace: true })
        }
      } catch (e) {
        console.error('菜单树解析失败', e)
      }
    }
    // 无论是否成功加载，都标记为已加载，避免死循环
    routesLoaded = true
  }

  // 6. 任务C：动态路由未匹配兜底——已授权但未注册（无权限）→ 403；未知路径 → 404（不再白屏）
  // 注：不加 '*' 通配路由——动态路由 addRoutes 为追加注册，通配会抢先匹配（任务书该行有缺陷，已规避）
  if (to.matched.length === 0) {
    const paths = await loadAllMenuPaths()
    return next(paths.includes(to.path) ? '/403' : '/404')
  }
  // 5. 正常放行
  next()
})

export default router